import { gql } from 'graphql.macro';
import { customerSettingsFragment, orgAddressFragment, orgContactFragment } from './common';

export const orgExportFragment = gql`
    fragment orgExportFields on Export {
        enable
        additionalDeliveryDays
    }
`;
export const orgCurrentJobFragment = gql`
    fragment orgCurrentJobFields on CurrentJob {
        name
        deliveryDays
        active
    }
`;

export const orgNotificationFragment = gql`
    fragment orgNotificationFields on OrgGeneralNotifications {
        newOrder
        newMessage
        orderModified
        orderResend
        orderRated
        orderCancelled
        orderReceivedByLab
        orderSentByLab
        orderDeclined
        placedOrderNewMessage
        priceRequests
        invoiceBatches
    }
`;

export const orgEmailNotificationFragment = gql`
    fragment orgEmailNotificationFields on OrgEmailNotifications {
        type
        otherEmail
        newOrder
        newMessage
        orderModified
        orderRated
        orderCancelled
        orderResend
        orderReceivedByLab
        orderSentByLab
        orderDeclined
        placedOrderNewMessage
        priceRequests
        invoiceBatches
    }
`;

export const externalGeneralNotificationsFragment = gql`
    fragment externalGeneralNotificationsFields on ExternalGeneralNotifications {
        newInternalMessage
        internalOrderModified
        orderModified
        orderDeclined
        assignExtTech
    }
`;

export const externalEmailNotificationsFragment = gql`
    fragment externalEmailNotificationsFields on ExternalEmailNotifications {
        newInternalMessage
        internalOrderModified
        orderModified
        orderDeclined
        assignExtTech
    }
`;

export const localDentistEmailNotificationsFragment = gql`
    fragment localDentistEmailNotificationsFields on LocalDentistEmailNotifications {
        orderRecievedByLab
        orderSentByLab
    }
`;

export const connectedLabFragment = gql`
    fragment connectedLabFields on ConnectedLab {
        isDisabled
        isRequireException
        lab {
            _id
            name
            hexCode
            textCode
            logoSignedUrl
        }
    }
`;

export const invoiceConfigurationFragment = gql`
    fragment invoiceConfigurationFields on InvoiceConfiguration {
        invoiceNumberType
        currentStartingNumber
        monthlyInvoiceDay
        isShared
    }
`;

export const invoiceClinicFragment = gql`
    fragment invoiceClinicFields on InvoiceClinic {
        clinicId
        method
        paymentTerms
        invoice
    }
`;

export const orgInvoiceSettingFragment = gql`
    fragment invoicingFields on OrgInvoicingSetting {
        configuration {
            ...invoiceConfigurationFields
        }
        clinics {
            ...invoiceClinicFields
        }
        vatNo
        costCenter
        invoiceText
        invoiceAddress {
            ...orgAddressFields
        }
        communication {
            email
            phone
            site
        }
        paymentDetails {
            postgiro
            bankgiro
            bicSwift
            iban
            paymentBoxAccount
            accountNumber
            kid {
                enabled
                length
                checkDigitMethod
            }
            registrationNumber
            fi {
                enabled
                cardType
                creditorNumber
            }
            referenceFormat
        }
        showForetaksregisteret
        latePaymentInterest
        complaintPeriodDays
        taxNumber
        companyRegister {
            registeredOffice
            court
            number
            managingDirectors
        }
        smallBusinessExemption
        defaultSetting {
            invoiceType
            invoiceDelivery
            invoicePeriod
            paymentTerms
        }
    }
    ${invoiceConfigurationFragment}
    ${invoiceClinicFragment}
    ${orgAddressFragment}
`;

export const monetaryFragment = gql`
    fragment monetaryFields on Monetary {
        price
        currency
    }
`;

export const emailSettingsFragment = gql`
    fragment emailSettingsFields on EmailSettings {
        replyAddress
        to
        cc
        bcc
        subject
        body
        creditSubject
        creditBody
    }
`;

export const paymentDetailsFragment = gql`
    fragment paymentDetailsFields on PaymentDetails {
        postgiro
        bankgiro
        bicSwift
        iban
        vatNo
    }
`;

export const clientContentFragment = gql`
    fragment clientContentFields on ClientContent {
        name
        customerType
        active
        isMain
        contact {
            ...orgContactFields
        }
        isContactOverridden
        overriddenContact {
            ...orgContactFields
        }
    }
    ${orgContactFragment}
`;

export const invoiceSettingsFragment = gql`
    fragment invoiceSettingsFields on InvoiceSettings {
        type
        invoiceDelivery
        invoicePeriod
        emailSettings {
            ...emailSettingsFields
        }
        paymentTerms
        invoiceFee {
            ...monetaryFields
        }
        deliveryFee {
            ...monetaryFields
        }
        invoiceText
        fortnoxCustomerNo
        vismaCustomerId
        contactInformation {
            name
            email
            phone
        }
        showPatientPIN
        isDeliveryNoteAttached
        isDeliveryNotesMergedWithInvoice
        invoiceAddress {
            ...orgAddressFields
        }
        supplierReference
        isOverwritten
    }
    ${monetaryFragment}
    ${emailSettingsFragment}
    ${orgAddressFragment}
`;

export const stepsFragment = gql`
    fragment stepsFields on Steps {
        _id
        name
        description
        maxHourInStep
        defaultExtUsers {
            _id
            fullName
            name {
                firstName
                lastName
            }
        }
        useMillingDeadline
        millingDeadlineDays
        initiatesSubcontract
    }
`;

export const workFlowFragment = gql`
    fragment workFlowFields on WorkFlow {
        _id
        steps {
            ...stepsFields
        }
        defaultNoSample
        defaultPhysicalSample
        defaultDigitalSample
    }
    ${stepsFragment}
`;

export const deviationTypesFragment = gql`
    fragment deviationTypesFields on DeviationType {
        _id
        code
        description
        type
    }
`;

export const listViewProfileFragment = gql`
    fragment listViewProfileFields on ListViewProfile {
        _id
        name
        listType
        columns {
            key
            visible
        }
        showIconArea
        defaultSortField
        defaultSortDirection
    }
`;

export const orderListProfileDefaultsFragment = gql`
    fragment orderListProfileDefaultsFields on OrderListProfileDefaults {
        mainProfileId
        workflowProfileId
    }
`;

export const organizationLabSettingsFragment = gql`
    fragment organizationLabSettingsFields on Organization {
        _id
        name
        publishLab
        shortDescription
        longDescription
        profileTexts {
            language
            shortDescription
            longDescription
        }
        supplierProfile {
            visible
            services
            otherService
            texts {
                language
                description
            }
        }
        hexCode
        textCode
        logoImage
        staffImage
        logoSignedUrl
        staffSignedUrl
        currentJobs {
            ...orgCurrentJobFields
        }
        export {
            ...orgExportFields
        }
        contact {
            ...orgContactFields
        }
        generalNotifications {
            ...orgNotificationFields
        }
        emailNotifications {
            ...orgEmailNotificationFields
        }
        externalGeneralNotifications {
            ...externalGeneralNotificationsFields
        }
        externalEmailNotifications {
            ...externalEmailNotificationsFields
        }
        localDentistEmailNotifications {
            ...localDentistEmailNotificationsFields
        }
        notificationListDefaults {
            showOnlyUnread
            showNotDone
        }
        openingHours {
            day
            time {
                from
                to
            }
            active
        }
        holidayDays
        unavailableLabDates
        isTransportEnable
        isApiEnabled
        transportSettings {
            nShiftTransport {
                waybillPrintFormat
            }
        }
        sourceCurrency
        localeFormat {
            language
            date
            number
        }
    }
    ${orgExportFragment}
    ${orgCurrentJobFragment}
    ${orgContactFragment}
    ${orgNotificationFragment}
    ${orgEmailNotificationFragment}
    ${externalGeneralNotificationsFragment}
    ${externalEmailNotificationsFragment}
    ${localDentistEmailNotificationsFragment}
`;

export const labInvoiceSettingsFragment = gql`
    fragment labInvoiceSettingsFields on Organization {
        _id
        name
        officialName
        organizationNumber
        contact {
            address {
                country
            }
        }
        invoicing {
            ...invoicingFields
        }
    }
    ${orgInvoiceSettingFragment}
`;

export const organizationFragment = gql`
    fragment organizationFields on Organization {
        _id
        name
        publishLab
        isWhiteLabeled
        export {
            ...orgExportFields
        }
        currentJobs {
            ...orgCurrentJobFields
        }
        generalNotifications {
            ...orgNotificationFields
        }
        emailNotifications {
            ...orgEmailNotificationFields
        }
        externalGeneralNotifications {
            ...externalGeneralNotificationsFields
        }
        externalEmailNotifications {
            ...externalEmailNotificationsFields
        }
        connectedLabs {
            ...connectedLabFields
        }
        customerSettings {
            ...customerSettingsFields
        }
        invoicing {
            ...invoicingFields
        }
        contact {
            ...orgContactFields
        }
        workFlow {
            ...workFlowFields
        }
        deviationTypes {
            ...deviationTypesFields
        }
        openingHours {
            day
            time {
                from
                to
            }
            active
        }
        holidayDays
        unavailableLabDates
        hexCode
        textCode
        longDescription
        shortDescription
        logoSignedUrl
        staffSignedUrl
        logoImage
        staffImage
        isTransportEnable
        isApiEnabled
        defaultActionListId
        defaultMaterialPriceListId
        defaultActionPriceListId
        orderTags {
            _id
            name
            color
        }
        organizationNumber
        activeActionSet
        actionSets {
            _id
            title
            currency
        }
    }
    ${orgNotificationFragment}
    ${orgEmailNotificationFragment}
    ${externalGeneralNotificationsFragment}
    ${externalEmailNotificationsFragment}
    ${connectedLabFragment}
    ${customerSettingsFragment}
    ${orgInvoiceSettingFragment}
    ${orgContactFragment}
    ${workFlowFragment}
    ${invoiceSettingsFragment}
    ${deviationTypesFragment}
    ${orgExportFragment}
    ${orgCurrentJobFragment}
`;

export const clinicFragment = gql`
    fragment clinicFields on Organization {
        _id
        name
        officialName
        organizationNumber
        gln
        parentClinicId
        contact {
            ...orgContactFields
        }
        invoicing {
            costCenter
            vatNo
            invoiceAddress {
                ...orgAddressFields
            }
            communication {
                email
            }
        }
        isNotPickUpAndDeliveryPlace
        active
        externalInfo {
            externalId1
            externalId2
        }
        created {
            on
        }
        customerSettings {
            ...customerSettingsFields
        }
    }
    ${orgAddressFragment}
    ${orgContactFragment}
    ${customerSettingsFragment}
`;
